import * as React from "react";
import { ActiveModifiers, DayPicker } from "react-day-picker";
import { useKeyboard } from "@react-aria/interactions";
import { useMachine } from "@xstate/react";
import pl from "date-fns/locale/pl";
import { IAppointment, ISlot } from "@nju/data-access-portal-appointment";
import { IFtthOrchestrator } from "@nju/data-access-portal-orchestrator";
import { IScope } from "@nju/scope";
import { Loader } from "@nju/ui";
import { calendarMachine } from "./calendar.machine";
import { CalendarCaption } from "./CalendarCaption";
import { EmptyCalendar } from "./EmptyCalendar";
import { Slots } from "./Slots";
import { dateFormat, disabledDaysInMonth } from "./utils";

interface ICalendarProps {
  ftthOrchestrator: IFtthOrchestrator;
  onClose: () => void;
  onSlotSelect: (date: { date: string; slot: ISlot }) => void;
  selectedDate?: IAppointment;
  onCalendarError: () => void;
  variant: IScope;
}

const titleCss =
  "pt-2 color-daintree mb-3 opacity-50 text-[14px] from-tablet:hidden";

export const Calendar = React.forwardRef<HTMLDivElement, ICalendarProps>(
  function Calendar(
    {
      onClose,
      onSlotSelect,
      selectedDate,
      onCalendarError,
      ftthOrchestrator,
      variant,
    }: ICalendarProps,
    ref,
  ) {
    const { installationToken } = ftthOrchestrator;
    const [calendarDate, setCalendarDate] = React.useState(
      dateFormat(new Date()),
    );

    const calendarMonthCallback = (date: Date) => {
      return setCalendarDate(dateFormat(date));
    };

    const { keyboardProps } = useKeyboard({
      onKeyDown: (event) => {
        if (event.key === "Escape") {
          onClose();
        }
      },
    });

    const [current, send] = useMachine(calendarMachine, {
      actions: {
        onCalendarError,
      },
      context: {
        installationToken: installationToken || "",
        selectedDate,
      },
    });

    const {
      availableDates,
      selectedDay,
      availableSlots,
      selectedSlot,
      disabledDays,
    } = current.context;

    const handleOnDayClick = React.useCallback(
      (day: Date, modifiers: ActiveModifiers) => {
        if (modifiers.disabled) {
          return;
        }

        send({ type: "SELECT_DAY", value: { day, modifiers } });
      },
      [send],
    );

    const handleOnSlotClick = React.useCallback(
      (slot: ISlot) => {
        if (!selectedDay) {
          return;
        }

        send({ type: "SELECT_SLOT", value: slot });
        onSlotSelect({ date: selectedDay?.toLocaleDateString(), slot });
      },
      [onSlotSelect, selectedDay, send],
    );

    const availableDatesInMonth = disabledDaysInMonth(
      availableDates,
      calendarDate,
    );

    return (
      <div
        className="px-3 py-6 fixed top-0 bottom-0 left-0 right-0 z-[300] bg-white shadow-xl flex-col flex
        from-tablet:left-[auto] from-tablet:bottom-[auto] from-tablet:flex-row from-tablet:rounded-[12px]
        from-tablet:p-4 from-tablet:absolute from-tablet:top-[120%] from-tablet:right-0 only-mobile:items-center"
        data-cy="calendar"
        ref={ref}
        {...keyboardProps}
      >
        {/* Attention! These styles were modified so take caution whenever You will want to upgrade this calendar! */}
        <style>{`
    .rdp {
      --rdp-cell-size: 30px;
      --rdp-accent-color: hsl(188, 100%, 40%);
      --rdp-background-color: hsl(188, 100%, 40%);
      --rdp-accent-color-dark: hsl(188, 100%, 40%);
      --rdp-background-color-dark: hsl(188, 100%, 40%);
      --rdp-outline: 2px solid var(--rdp-accent-color); /* Outline border for focused elements */
      --rdp-outline-selected: 3px solid var(--rdp-accent-color); /* Outline border for focused _and_ selected elements */
    }

    /* Hide elements for devices that are not screen readers */
    .rdp-vhidden {
      box-sizing: border-box;
      padding: 0;
      margin: 0;
      background: transparent;
      border: 0;
      -moz-appearance: none;
      -webkit-appearance: none;
      appearance: none;
      position: absolute !important;
      top: 0;
      width: 1px !important;
      height: 1px !important;
      padding: 0 !important;
      overflow: hidden !important;
      clip: rect(1px, 1px, 1px, 1px) !important;
      border: 0 !important;
    }

    /* Buttons */
    .rdp-button_reset {
      appearance: none;
      position: relative;
      margin: 0;
      padding: 0;
      cursor: default;
      color: inherit;
      background: none;
      font: inherit;

      -moz-appearance: none;
      -webkit-appearance: none;
    }

    .rdp-button_reset:focus-visible {
      /* Make sure to reset outline only when :focus-visible is supported */
      outline: none;
    }

    .rdp-button {
      border: 2px solid transparent;
    }

    .rdp-button[disabled]:not(.rdp-day_selected) {
      opacity: 0.25;
    }

    .rdp-button:not([disabled]) {
      cursor: pointer;
    }

    .rdp-button:focus-visible:not([disabled]) {
      color: inherit;
      background-color: var(--rdp-background-color);
      border: var(--rdp-outline);
    }

    .rdp-button:hover:not([disabled]):not(.rdp-day_selected) {
      background-color: var(--rdp-background-color);
      color: white;
    }

    .rdp-months {
      display: flex;
    }

    .rdp-month {
      margin: 0 1em;
    }

    .rdp-month:first-child {
      margin-left: 0;
    }

    .rdp-month:last-child {
      margin-right: 0;
    }

    .rdp-table {
      margin: 0;
      max-width: calc(var(--rdp-cell-size) * 7);
      border-collapse: collapse;
    }

    .rdp-with_weeknumber .rdp-table {
      max-width: calc(var(--rdp-cell-size) * 8);
      border-collapse: collapse;
    }

    .rdp-caption {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0;
      text-align: left;
    }

    .rdp-multiple_months .rdp-caption {
      position: relative;
      display: block;
      text-align: center;
    }

    .rdp-caption_dropdowns {
      position: relative;
      display: inline-flex;
    }

    .rdp-caption_label {
      position: relative;
      z-index: 1;
      display: inline-flex;
      align-items: center;
      margin: 0;
      padding: 0 0.25em;
      white-space: nowrap;
      color: currentColor;
      border: 0;
      border: 2px solid transparent;
      font-family: inherit;
      font-size: 140%;
      font-weight: bold;
    }

    .rdp-nav {
      white-space: nowrap;
    }

    .rdp-multiple_months .rdp-caption_start .rdp-nav {
      position: absolute;
      top: 50%;
      left: 0;
      transform: translateY(-50%);
    }

    .rdp-multiple_months .rdp-caption_end .rdp-nav {
      position: absolute;
      top: 50%;
      right: 0;
      transform: translateY(-50%);
    }

    .rdp-nav_button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: var(--rdp-cell-size);
      height: var(--rdp-cell-size);
      padding: 0.25em;
      border-radius: 100%;
    }

    /* ---------- */
    /* Dropdowns  */
    /* ---------- */

    .rdp-dropdown_year,
    .rdp-dropdown_month {
      position: relative;
      display: inline-flex;
      align-items: center;
    }

    .rdp-dropdown {
      appearance: none;
      position: absolute;
      z-index: 2;
      top: 0;
      bottom: 0;
      left: 0;
      width: 100%;
      margin: 0;
      padding: 0;
      cursor: inherit;
      opacity: 0;
      border: none;
      background-color: transparent;
      font-family: inherit;
      font-size: inherit;
      line-height: inherit;
    }

    .rdp-dropdown[disabled] {
      opacity: unset;
      color: unset;
    }

    .rdp-dropdown:focus-visible:not([disabled]) + .rdp-caption_label {
      background-color: var(--rdp-background-color);
      border: var(--rdp-outline);
      border-radius: 6px;
    }

    .rdp-dropdown_icon {
      margin: 0 0 0 5px;
    }

    .rdp-head {
      border: 0;
    }

    .rdp-head_row,
    .rdp-row {
      height: 100%;
    }

    .rdp-head_cell {
      vertical-align: middle;
      text-transform: capitalize;
      font-size: 0.75em;
      font-weight: 700;
      text-align: center;
      height: 100%;
      height: var(--rdp-cell-size);
      padding: 0;
    }

    .rdp-tbody {
      border: 0;
    }

    .rdp-tfoot {
      margin: 0.5em;
    }

    .rdp-cell {
      width: var(--rdp-cell-size);
      height: 100%;
      height: var(--rdp-cell-size);
      padding: 0px 5px;
      text-align: center;
    }

    .rdp-weeknumber {
      font-size: 0.75em;
    }

    .rdp-weeknumber,
    .rdp-day {
      display: flex;
      overflow: hidden;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;
      width: var(--rdp-cell-size);
      max-width: var(--rdp-cell-size);
      height: var(--rdp-cell-size);
      margin: 0;
      border: 2px solid transparent;
      border-radius: 100%;
    }

    .rdp-day_today:not(.rdp-day_outside) {
      font-weight: bold;
    }

    .rdp-day_selected,
    .rdp-day_selected:focus-visible,
    .rdp-day_selected:hover {
      color: white;
      opacity: 1;
      background-color: var(--rdp-accent-color);
    }

    .rdp-day_selected:focus-visible {
      /* Since the background is the same use again the outline */
      outline: var(--rdp-outline);
      outline-offset: 2px;
      z-index: 1;
    }

    .rdp:not([dir="rtl"]) .rdp-day_range_start:not(.rdp-day_range_end) {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }

    .rdp:not([dir="rtl"]) .rdp-day_range_end:not(.rdp-day_range_start) {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }

    .rdp[dir="rtl"] .rdp-day_range_start:not(.rdp-day_range_end) {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }

    .rdp[dir="rtl"] .rdp-day_range_end:not(.rdp-day_range_start) {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }

    .rdp-day_range_end.rdp-day_range_start {
      border-radius: 100%;
    }

    .rdp-day_range_middle {
      border-radius: 0;
    }

    .selected {
      background-color: hsl(188, 100%, 40%);
      font-weight: bold;
      color: white;
    }
    
    .rdp-day_outside {
      opacity: 0.25;
      pointer-events: none;
    }
  `}</style>
        {availableDates.length > 0 && (
          <span className={titleCss}>Wybierz dzień</span>
        )}

        <div
          className="absolute right-7 top-3 cursor-pointer from-tablet:hidden"
          onClick={onClose}
        >
          <span>zamknij</span>
        </div>
        {availableDates.length > 0 && (
          <DayPicker
            components={{
              Caption: CalendarCaption,
            }}
            data-cy="calendar"
            disabled={[
              ...availableDatesInMonth.map((day: string) => {
                return new Date(day);
              }),
              ...disabledDays,
            ]}
            locale={pl}
            modifiersClassNames={{
              selected: "selected",
            }}
            onDayClick={handleOnDayClick}
            onMonthChange={calendarMonthCallback}
            selected={selectedDay}
            showOutsideDays={true}
          />
        )}
        {current.matches("loading") && (
          <div className="h-full flex items-center justify-center from-tablet:w-[280px] from-tablet:h-[249px]">
            <Loader />
          </div>
        )}
        {!current.matches("loading") && availableDates.length === 0 && (
          <EmptyCalendar variant={variant} />
        )}
        {availableSlots.length > 0 && (
          <div className="mt-3">
            <span className={titleCss}>Wybierz godziny</span>
            <Slots
              onArrowDown={() => {
                send({ type: "ARROW", name: "down" });
              }}
              onArrowUp={() => {
                send({ type: "ARROW", name: "up" });
              }}
              onSlotSelect={handleOnSlotClick}
              selectedSlot={selectedSlot}
              slots={availableSlots}
            />
          </div>
        )}
      </div>
    );
  },
);
